/**
 * Mood stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const moodStickersPack: ToilePack = {
    slug: 'mood-stickers',
    category: 'sticker',
    name: 'Mood stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'happy', label: 'Sunny', subject: 'a round sun face with a wide smile'},
        {key: 'sad', label: 'Rainy', subject: 'a round cloud face with rain and a small frown'},
        {key: 'angry', label: 'Stormy', subject: 'a round dark cloud face with lightning and a scowl'},
    ],
    styles: [
        {key: 'chat', label: 'Chat sticker', style: 'messaging-app sticker, expressive cartoon, thick outline, bold flat colour', palette: 'Bright flat colour with a white outline', framing: 'The sticker isolated on a plain ground with a white outline, built to read at chat size'},
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a calm face instead', 'a whole sticker sheet', 'in pastel colors'],
    directionHint: 'try: a calm face instead',
    seo: {
        title: 'Mood Stickers: Happy, Sad, Angry Tracker Faces',
        metaDescription: 'Nine mood tracker stickers — a sunny happy face, a rainy sad face, a stormy angry face — in chat, kawaii, and doodle styles.',
        h1: 'Mood stickers: happy, sad, and angry tracker faces in three styles',
        tagline: 'Nine mood stickers, three feelings, three styles each.',
        intro: 'The mood for the tracker: a sunny happy face, a rainy sad face, a stormy angry face — weather standing in for feeling, the way most mood charts already work. Rendered chat, kawaii, and doodle so the set fits a bullet journal, a classroom feelings chart, or a group chat alike. Ask for another mood — calm, excited, tired — and the same three styles carry it.',
        useCases: [
            'a mood tracker spread in a planner or journal',
            'a classroom feelings chart',
            'a therapy or self-care worksheet sticker',
            'a chat sticker pack for expressing mood',
            'a printable emotion-check sticker sheet',
        ],
        faq: [
            {q: 'Do the weather icons stand for specific moods?', a: 'Yes — sunny reads as happy, rainy as sad, and stormy as angry, the common visual shorthand mood trackers use.'},
            {q: 'Can I get other moods, like calm or excited?', a: 'Yes — the direction field takes any mood or feeling, rendered in the same three styles.'},
            {q: "What's the difference between chat, kawaii, and doodle?", a: 'Chat is a rounded messaging-app character style, kawaii is soft and big-eyed, and doodle is a hand-drawn sketch look — pick whichever matches your spread or chat aesthetic.'},
            {q: 'Are these meant for a therapy or classroom feelings chart?', a: "Yes — the set works equally well as a personal mood tracker, a classroom check-in board, or a therapist's worksheet sticker."},
            {q: 'Can I use these in a resource I share with a class or clients?', a: 'Personal and classroom use is fine; check the license terms before reselling the designs themselves.'},
        ],
    },
}
